import { memo, useState } from 'react'
import { projects } from '../data/projects'
import type { ProjectFilter } from '../types'

export const Projects = memo(function Projects() {
  const [activeFilter, setActiveFilter] = useState<ProjectFilter>('all')

  const filteredProjects =
    activeFilter === 'all'
      ? projects
      : projects.filter((project) => {
          if (activeFilter === 'ai') {
            return project.category.includes('ai') || project.category.includes('ml')
          }
          return project.category.includes(activeFilter)
        })

  return (
<section id="projects" className="section-bento projects-bento">
  <div className="container-bento">
    <div className="section-header-bento">
      <span className="section-label-bento">Portfolio</span>
      <h2 className="section-title-bento">Featured Projects</h2>
    </div>

    {/* Filter Buttons */}
    <div className="filter-buttons-bento">
      <button
        className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
        onClick={() => setActiveFilter('all')}
      >
        All Projects
      </button>
      <button
        className={`filter-btn ${activeFilter === 'web' ? 'active' : ''}`}
        onClick={() => setActiveFilter('web')}
      >
        Web
      </button>
      <button
        className={`filter-btn ${activeFilter === 'mobile' ? 'active' : ''}`}
        onClick={() => setActiveFilter('mobile')}
      >
        Mobile
      </button>
      <button
        className={`filter-btn ${activeFilter === 'ai' ? 'active' : ''}`}
        onClick={() => setActiveFilter('ai')}
      >
        AI/ML
      </button>
    </div>

    {/* Projects Grid */}
    <div className="bento-grid projects-grid">
      {filteredProjects.map((project) => (
        <div key={project.id} className="bento-card project-card-bento">
          <div className="project-header-bento">
            {project.image === 'nike-logo' ? (
              <div className="project-nike-logo">
                <svg viewBox="0 0 69 32" className="nike-logo-project">
                  <path
                    fill="currentColor"
                    d="M68.56 4L18.4 25.36Q12.16 28 7.92 28q-4.8 0-6.96-3.36-1.36-2.16-.8-5.48t2.96-7.08q2-3.04 6.56-8-1.6 2.56-2.24 5.28-1.2 5.12 2.4 7.52Q14.24 19 21.52 19q4.4 0 16.64-3.52L68.56 4z"
                  />
                </svg>
              </div>
            ) : project.image === 'car-emissions-logo' ? (
              <div className="project-car-emissions-logo">
                <svg
                  viewBox="0 0 80 80"
                  className="car-emissions-logo-project"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Gradient Definitions */}
                  <defs>
                    <linearGradient id="carGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#efefef', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#cdcdcd', stopOpacity: 1 }} />
                    </linearGradient>
                    <linearGradient id="exhaustGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#94a3b8', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#64748b', stopOpacity: 1 }} />
                    </linearGradient>
                    <filter id="carGlow">
                      <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                      <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>

                  {/* Road Lines (animated to show movement) */}
                  <g className="road-lines">
                    <line
                      x1="0"
                      y1="62"
                      x2="20"
                      y2="62"
                      stroke="#475569"
                      strokeWidth="1"
                      strokeDasharray="8,4"
                      className="road-line"
                      opacity="0.5"
                    />
                    <line
                      x1="20"
                      y1="62"
                      x2="40"
                      y2="62"
                      stroke="#475569"
                      strokeWidth="1"
                      strokeDasharray="8,4"
                      className="road-line"
                      opacity="0.5"
                    />
                    <line
                      x1="40"
                      y1="62"
                      x2="60"
                      y2="62"
                      stroke="#475569"
                      strokeWidth="1"
                      strokeDasharray="8,4"
                      className="road-line"
                      opacity="0.5"
                    />
                    <line
                      x1="60"
                      y1="62"
                      x2="80"
                      y2="62"
                      stroke="#475569"
                      strokeWidth="1"
                      strokeDasharray="8,4"
                      className="road-line"
                      opacity="0.5"
                    />
                  </g>

                  {/* Car Container (entire car bounces) */}
                  <g className="car-container">
                    {/* Car Body */}
                    <g className="car-body">
                      {/* Main body */}
                      <path
                        d="M 15 45 L 20 35 L 30 30 L 55 30 L 60 35 L 65 45 L 65 55 L 15 55 Z"
                        fill="url(#carGradient)"
                        filter="url(#carGlow)"
                      />

                      {/* Windshield */}
                      <path
                        d="M 32 35 L 38 32 L 50 32 L 54 35 Z"
                        fill="#60a5fa"
                        opacity="0.6"
                      />

                      {/* Side window */}
                      <path
                        d="M 56 36 L 60 37 L 60 43 L 56 44 Z"
                        fill="#60a5fa"
                        opacity="0.5"
                      />

                      {/* Front light */}
                      <circle cx="62" cy="42" r="2" fill="#fbbf24" opacity="0.8">
                        <animate
                          attributeName="opacity"
                          values="0.8;1;0.8"
                          dur="1s"
                          repeatCount="indefinite"
                        />
                      </circle>

                      {/* Door line */}
                      <line
                        x1="42"
                        y1="35"
                        x2="42"
                        y2="52"
                        stroke="#16a34a"
                        strokeWidth="1"
                        opacity="0.5"
                      />

                      {/* Spoiler detail */}
                      <path
                        d="M 14 48 L 14 52 L 15 52"
                        stroke="#16a34a"
                        strokeWidth="1"
                        fill="none"
                        opacity="0.7"
                      />
                    </g>

                    {/* Wheels with spinning animation */}
                    <g className="wheels">
                      {/* Front wheel */}
                      <g className="car-wheel" style={{ transformOrigin: '55px 55px' }}>
                        <circle cx="55" cy="55" r="6" fill="#334155" />
                        <circle cx="55" cy="55" r="4" fill="#475569" />
                        <circle cx="55" cy="55" r="2" fill="#64748b" />
                        <line
                          x1="55"
                          y1="51"
                          x2="55"
                          y2="59"
                          stroke="#94a3b8"
                          strokeWidth="1"
                        />
                        <line
                          x1="51"
                          y1="55"
                          x2="59"
                          y2="55"
                          stroke="#94a3b8"
                          strokeWidth="1"
                        />
                        <line
                          x1="52"
                          y1="52"
                          x2="58"
                          y2="58"
                          stroke="#94a3b8"
                          strokeWidth="0.5"
                        />
                        <line
                          x1="52"
                          y1="58"
                          x2="58"
                          y2="52"
                          stroke="#94a3b8"
                          strokeWidth="0.5"
                        />
                      </g>

                      {/* Back wheel */}
                      <g className="car-wheel" style={{ transformOrigin: '25px 55px' }}>
                        <circle cx="25" cy="55" r="6" fill="#334155" />
                        <circle cx="25" cy="55" r="4" fill="#475569" />
                        <circle cx="25" cy="55" r="2" fill="#64748b" />
                        <line
                          x1="25"
                          y1="51"
                          x2="25"
                          y2="59"
                          stroke="#94a3b8"
                          strokeWidth="1"
                        />
                        <line
                          x1="21"
                          y1="55"
                          x2="29"
                          y2="55"
                          stroke="#94a3b8"
                          strokeWidth="1"
                        />
                        <line
                          x1="22"
                          y1="52"
                          x2="28"
                          y2="58"
                          stroke="#94a3b8"
                          strokeWidth="0.5"
                        />
                        <line
                          x1="22"
                          y1="58"
                          x2="28"
                          y2="52"
                          stroke="#94a3b8"
                          strokeWidth="0.5"
                        />
                      </g>
                    </g>

                    {/* Exhaust Pipe */}
                    <rect
                      x="12"
                      y="50"
                      width="5"
                      height="3"
                      rx="1"
                      fill="url(#exhaustGradient)"
                      className="exhaust-pipe"
                    />

                    {/* Emission Particles (multiple layers for depth) */}
                    <g className="emission-particles">
                      <circle
                        cx="10"
                        cy="51"
                        r="1.5"
                        fill="#64748b"
                        className="emission-particle"
                        opacity="0.6"
                        style={{ animationDelay: '0s' }}
                      />
                      <circle
                        cx="10"
                        cy="51"
                        r="2"
                        fill="#94a3b8"
                        className="emission-particle"
                        opacity="0.4"
                        style={{ animationDelay: '0.3s' }}
                      />
                      <circle
                        cx="10"
                        cy="51"
                        r="1.2"
                        fill="#475569"
                        className="emission-particle"
                        opacity="0.5"
                        style={{ animationDelay: '0.6s' }}
                      />
                      <circle
                        cx="10"
                        cy="51"
                        r="1.8"
                        fill="#cbd5e1"
                        className="emission-particle"
                        opacity="0.3"
                        style={{ animationDelay: '0.9s' }}
                      />
                      <circle
                        cx="10"
                        cy="51"
                        r="1.3"
                        fill="#64748b"
                        className="emission-particle"
                        opacity="0.5"
                        style={{ animationDelay: '1.2s' }}
                      />
                    </g>
                  </g>

                  {/* ML Chip Indicator (top right) */}
                  <g className="ml-chip" transform="translate(58, 10)">
                    <rect
                      x="0"
                      y="0"
                      width="14"
                      height="10"
                      rx="2"
                      fill="#1e293b"
                      stroke="#22c55e"
                      strokeWidth="1"
                    />
                    <line x1="2" y1="3" x2="12" y2="3" stroke="#22c55e" strokeWidth="0.5" />
                    <line x1="2" y1="5" x2="10" y2="5" stroke="#22c55e" strokeWidth="0.5" />
                    <line x1="2" y1="7" x2="12" y2="7" stroke="#22c55e" strokeWidth="0.5" />
                    <text
                      x="7"
                      y="9"
                      textAnchor="middle"
                      fill="#22c55e"
                      fontSize="4"
                      fontWeight="600"
                    >
                      ML
                    </text>
                  </g>

                  {/* Classification Indicator (gauge/meter) */}
                  <g className="classification-meter" transform="translate(12, 12)">
                    <circle
                      cx="0"
                      cy="0"
                      r="6"
                      fill="none"
                      stroke="#334155"
                      strokeWidth="1.5"
                    />
                    <circle
                      cx="0"
                      cy="0"
                      r="6"
                      fill="none"
                      stroke="#22c55e"
                      strokeWidth="1.5"
                      strokeDasharray="18.84"
                      strokeDashoffset="4.71"
                      className="meter-progress"
                    />
                    <text
                      x="0"
                      y="2"
                      textAnchor="middle"
                      fill="#22c55e"
                      fontSize="4"
                      fontWeight="700"
                    >
                      95%
                    </text>
                  </g>

                  {/* Pass/Fail Badge */}
                  <g className="status-badge" transform="translate(35, 15)">
                    <rect
                      x="0"
                      y="0"
                      width="20"
                      height="6"
                      rx="3"
                      fill="#065f46"
                      opacity="0.3"
                    />
                    <text
                      x="10"
                      y="4.5"
                      textAnchor="middle"
                      fill="#22c55e"
                      fontSize="4"
                      fontWeight="700"
                    >
                      PASS
                    </text>
                  </g>

                  {/* Speed lines (motion blur effect) */}
                  <g className="speed-lines" opacity="0.3">
                    <line
                      x1="5"
                      y1="38"
                      x2="12"
                      y2="38"
                      stroke="#22c55e"
                      strokeWidth="0.5"
                      className="road-line"
                    />
                    <line
                      x1="3"
                      y1="42"
                      x2="10"
                      y2="42"
                      stroke="#22c55e"
                      strokeWidth="0.5"
                      className="road-line"
                    />
                    <line
                      x1="6"
                      y1="46"
                      x2="13"
                      y2="46"
                      stroke="#22c55e"
                      strokeWidth="0.5"
                      className="road-line"
                    />
                  </g>
                </svg>
              </div>
            ) : project.image === 'spirit-logo' ? (
              <div className="project-spirit-logo">
                <img
                  src="https://spiritsync.app/assets/assets/images/logo-circle.5e5284cb81135c2148045019f0f39983.png"
                  alt="SpiritSync AI"
                  className="spirit-logo-project"
                />
              </div>
            ) : project.image === 'disney-logo' ? (
              <div className="project-disney-logo">
                <svg
                  viewBox="0 0 200 60"
                  className="disney-logo-project"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Background */}
                  <defs>
                    <linearGradient id="disneyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#359aaa', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#0E2F8F', stopOpacity: 1 }} />
                    </linearGradient>
                  </defs>

                  {/* Disney Text - Simplified */}
                  <text
                    x="10"
                    y="38"
                    fill="white"
                    fontSize="32"
                    fontWeight="bold"
                    fontFamily="Arial, sans-serif"
                    className="disney-text-main"
                  >
                    Disney
                  </text>

                  {/* Plus Circle */}
                  <circle
                    cx="168"
                    cy="28"
                    r="16"
                    fill="url(#disneyGradient)"
                    className="plus-circle"
                  />

                  {/* Plus Symbol */}
                  <g className="plus-symbol">
                    <line
                      x1="168"
                      y1="18"
                      x2="168"
                      y2="38"
                      stroke="white"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                    />
                    <line
                      x1="158"
                      y1="28"
                      x2="178"
                      y2="28"
                      stroke="white"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                    />
                  </g>
                </svg>
              </div>
            ) : project.image === 'plinko-logo' ? (
              <div className="project-plinko-logo">
                <svg
                  viewBox="0 0 80 80"
                  className="plinko-logo-project"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Gradient Definitions */}
                  <defs>
                    <linearGradient id="plinkoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#f59e0b', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#dc2626', stopOpacity: 1 }} />
                    </linearGradient>
                    <linearGradient id="ballGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#fbbf24', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#f59e0b', stopOpacity: 1 }} />
                    </linearGradient>
                    <filter id="glow">
                      <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                      <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>

                  {/* Plinko Board Triangle */}
                  <path
                    d="M40 10 L70 65 L10 65 Z"
                    fill="none"
                    stroke="url(#plinkoGradient)"
                    strokeWidth="2.5"
                    className="plinko-triangle"
                  />

                  {/* Pegs - arranged in triangle pattern */}
                  <circle cx="40" cy="22" r="2.5" fill="#6366f1" className="peg" />
                  <circle cx="32" cy="32" r="2.5" fill="#6366f1" className="peg" />
                  <circle cx="48" cy="32" r="2.5" fill="#6366f1" className="peg" />
                  <circle cx="24" cy="42" r="2.5" fill="#6366f1" className="peg" />
                  <circle cx="40" cy="42" r="2.5" fill="#6366f1" className="peg" />
                  <circle cx="56" cy="42" r="2.5" fill="#6366f1" className="peg" />
                  <circle cx="16" cy="52" r="2.5" fill="#6366f1" className="peg" />
                  <circle cx="32" cy="52" r="2.5" fill="#6366f1" className="peg" />
                  <circle cx="48" cy="52" r="2.5" fill="#6366f1" className="peg" />
                  <circle cx="64" cy="52" r="2.5" fill="#6366f1" className="peg" />

                  {/* Ball with gradient and glow */}
                  <circle
                    cx="40"
                    cy="15"
                    r="5"
                    fill="url(#ballGradient)"
                    filter="url(#glow)"
                    className="plinko-ball"
                  />

                  {/* ML Brain Icon */}
                  <g className="ml-brain" transform="translate(55, 8)">
                    <circle
                      cx="0"
                      cy="0"
                      r="8"
                      fill="none"
                      stroke="#8b5cf6"
                      strokeWidth="1.5"
                    />
                    <circle cx="-3" cy="-2" r="1.5" fill="#8b5cf6" />
                    <circle cx="3" cy="-2" r="1.5" fill="#8b5cf6" />
                    <circle cx="0" cy="2" r="1.5" fill="#8b5cf6" />
                    <line x1="-3" y1="-2" x2="0" y2="2" stroke="#8b5cf6" strokeWidth="1" />
                    <line x1="3" y1="-2" x2="0" y2="2" stroke="#8b5cf6" strokeWidth="1" />
                  </g>
                </svg>
              </div>
            ) : project.image === 'knn-logo' ? (
              <div className="project-knn-logo">
                <svg
                  viewBox="0 0 80 80"
                  className="knn-logo-project"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Gradient Definitions */}
                  <defs>
                    <linearGradient id="knnGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#6366f1', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#8b5cf6', stopOpacity: 1 }} />
                    </linearGradient>
                    <linearGradient id="targetGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#fbbf24', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#f59e0b', stopOpacity: 1 }} />
                    </linearGradient>
                    <filter id="knnGlow">
                      <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                      <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>

                  {/* Grid Background */}
                  <g className="knn-grid">
                    <line
                      x1="20"
                      y1="0"
                      x2="20"
                      y2="80"
                      className="grid-line"
                      strokeWidth="0.5"
                    />
                    <line
                      x1="40"
                      y1="0"
                      x2="40"
                      y2="80"
                      className="grid-line"
                      strokeWidth="0.5"
                    />
                    <line
                      x1="60"
                      y1="0"
                      x2="60"
                      y2="80"
                      className="grid-line"
                      strokeWidth="0.5"
                    />
                    <line
                      x1="0"
                      y1="20"
                      x2="80"
                      y2="20"
                      className="grid-line"
                      strokeWidth="0.5"
                    />
                    <line
                      x1="0"
                      y1="40"
                      x2="80"
                      y2="40"
                      className="grid-line"
                      strokeWidth="0.5"
                    />
                    <line
                      x1="0"
                      y1="60"
                      x2="80"
                      y2="60"
                      className="grid-line"
                      strokeWidth="0.5"
                    />
                  </g>

                  {/* Data Points - Cluster 1 (Purple - Top Left) */}
                  <circle cx="22" cy="18" r="2.5" className="data-point point-cluster-1" />
                  <circle cx="18" cy="25" r="2.5" className="data-point point-cluster-1" />
                  <circle cx="28" cy="22" r="2.5" className="data-point point-cluster-1" />
                  <circle cx="25" cy="30" r="2.5" className="data-point point-cluster-1" />
                  <circle cx="32" cy="28" r="2.5" className="data-point point-cluster-1" />

                  {/* Data Points - Cluster 2 (Pink - Top Right) */}
                  <circle cx="58" cy="15" r="2.5" className="data-point point-cluster-2" />
                  <circle cx="62" cy="22" r="2.5" className="data-point point-cluster-2" />
                  <circle cx="55" cy="25" r="2.5" className="data-point point-cluster-2" />
                  <circle cx="65" cy="30" r="2.5" className="data-point point-cluster-2" />
                  <circle cx="60" cy="32" r="2.5" className="data-point point-cluster-2" />

                  {/* Data Points - Cluster 3 (Green - Bottom) */}
                  <circle cx="20" cy="55" r="2.5" className="data-point point-cluster-3" />
                  <circle cx="28" cy="58" r="2.5" className="data-point point-cluster-3" />
                  <circle cx="25" cy="65" r="2.5" className="data-point point-cluster-3" />
                  <circle cx="35" cy="60" r="2.5" className="data-point point-cluster-3" />
                  <circle cx="30" cy="68" r="2.5" className="data-point point-cluster-3" />

                  {/* Target Point (What we're predicting) */}
                  <circle
                    cx="40"
                    cy="40"
                    r="4"
                    className="target-point"
                    filter="url(#knnGlow)"
                  />

                  {/* K-Nearest Neighbor Lines (connecting to 3 nearest points) */}
                  <line
                    x1="40"
                    y1="40"
                    x2="32"
                    y2="28"
                    className="k-line k-line-1"
                    strokeWidth="1.5"
                  />
                  <line
                    x1="40"
                    y1="40"
                    x2="28"
                    y2="22"
                    className="k-line k-line-2"
                    strokeWidth="1.5"
                  />
                  <line
                    x1="40"
                    y1="40"
                    x2="25"
                    y2="30"
                    className="k-line k-line-3"
                    strokeWidth="1.5"
                  />

                  {/* Prediction Circle (expanding from target) */}
                  <circle
                    cx="40"
                    cy="40"
                    r="0"
                    className="prediction-circle"
                    strokeWidth="1"
                  />

                  {/* K Value Indicator */}
                  <g className="k-indicator">
                    <circle
                      cx="68"
                      cy="12"
                      r="8"
                      fill="#1e1b4b"
                      stroke="#fbbf24"
                      strokeWidth="1.5"
                    />
                    <text
                      x="68"
                      y="16"
                      textAnchor="middle"
                      className="k-text"
                      fontSize="10"
                      fontWeight="bold"
                    >
                      K=3
                    </text>
                  </g>

                  {/* Accuracy Meter (bottom right) */}
                  <g className="accuracy-meter">
                    <rect
                      x="52"
                      y="68"
                      width="25"
                      height="4"
                      fill="#1e1b4b"
                      stroke="#4f46e5"
                      strokeWidth="0.5"
                      rx="2"
                    />
                    <rect x="52" y="68" width="0" height="4" className="accuracy-bar" rx="2">
                      <animate
                        attributeName="width"
                        from="0"
                        to="20"
                        dur="2s"
                        repeatCount="indefinite"
                      />
                    </rect>
                  </g>

                  {/* Algorithm Label */}
                  <text
                    x="6"
                    y="12"
                    fill="#8b5cf6"
                    fontSize="8"
                    fontWeight="600"
                    opacity="0.8"
                  >
                    KNN
                  </text>
                </svg>
              </div>
            ) : project.image === 'cloud-logo' ? (
              <div className="project-cloud-logo">
                <svg
                  viewBox="0 0 80 80"
                  className="cloud-logo-project"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Gradient Definitions */}
                  <defs>
                    <linearGradient id="cloudGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#60a5fa', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#3b82f6', stopOpacity: 1 }} />
                    </linearGradient>
                    <linearGradient id="serverGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#8b5cf6', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#6366f1', stopOpacity: 1 }} />
                    </linearGradient>
                    <filter id="cloudGlow">
                      <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                      <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>

                  {/* Main Cloud Shape */}
                  <g className="main-cloud" filter="url(#cloudGlow)">
                    <ellipse
                      cx="40"
                      cy="38"
                      rx="20"
                      ry="14"
                      fill="url(#cloudGradient)"
                      opacity="0.9"
                    />
                    <ellipse
                      cx="28"
                      cy="40"
                      rx="12"
                      ry="10"
                      fill="url(#cloudGradient)"
                      opacity="0.95"
                    />
                    <ellipse
                      cx="52"
                      cy="40"
                      rx="14"
                      ry="11"
                      fill="url(#cloudGradient)"
                      opacity="0.95"
                    />
                    <rect
                      x="22"
                      y="38"
                      width="36"
                      height="8"
                      fill="url(#cloudGradient)"
                      opacity="0.9"
                    />
                  </g>

                  {/* Server/Data Particles floating up */}
                  <g className="data-particles">
                    <circle
                      cx="30"
                      cy="55"
                      r="1.5"
                      fill="#60a5fa"
                      className="particle particle-1"
                    />
                    <circle
                      cx="40"
                      cy="58"
                      r="1.5"
                      fill="#3b82f6"
                      className="particle particle-2"
                    />
                    <circle
                      cx="50"
                      cy="56"
                      r="1.5"
                      fill="#60a5fa"
                      className="particle particle-3"
                    />
                    <rect
                      x="35"
                      y="60"
                      width="10"
                      height="2"
                      rx="1"
                      fill="#8b5cf6"
                      className="particle particle-4"
                    />
                    <rect
                      x="28"
                      y="63"
                      width="8"
                      height="2"
                      rx="1"
                      fill="#6366f1"
                      className="particle particle-5"
                    />
                    <rect
                      x="44"
                      y="62"
                      width="12"
                      height="2"
                      rx="1"
                      fill="#8b5cf6"
                      className="particle particle-6"
                    />
                  </g>

                  {/* Upload/Download Arrows */}
                  <g className="cloud-arrows">
                    <path
                      d="M 40 30 L 40 22 M 37 25 L 40 22 L 43 25"
                      stroke="#ffffff"
                      strokeWidth="2"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="upload-arrow"
                    />
                    <path
                      d="M 32 35 L 32 43 M 29 40 L 32 43 L 35 40"
                      stroke="#ffffff"
                      strokeWidth="1.5"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="download-arrow"
                      opacity="0.7"
                    />
                  </g>

                  {/* Vercel Triangle (bottom right corner) */}
                  <g className="vercel-badge" transform="translate(58, 58)">
                    <path d="M 0 8 L 4 0 L 8 8 Z" fill="#000000" />
                  </g>
                </svg>
              </div>
            ) : project.image === 'anna-logo' ? (
              <div className="project-anna-logo">
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/liam-portfolio.appspot.com/o/logo192.png?alt=media&token=b0b92e34-2f6e-4118-ad65-5b32ec7e1d18"
                  alt="Anna Ackerman"
                  className="anna-logo-project"
                />
              </div>
            ) : (
              <span className="project-emoji">{project.image}</span>
            )}
            <div className="project-categories-badges">
              {project.category.map((cat, idx) => (
                <div key={idx} className="project-category-badge">
                  {cat}
                </div>
              ))}
            </div>
          </div>

          {/* Skills */}
          <div className="project-skills-bento">
            {project.skills.map((skill, i) => (
              <span key={i} className="project-skill-tag">
                {skill}
              </span>
            ))}
          </div>

          {/* Title */}
          <h3 className="project-title-bento">{project.title}</h3>

          {/* Description */}
          <p className="project-desc-bento">{project.description}</p>

          {/* Stats */}
          <div className="project-stats-bento">
            {Object.entries(project.stats).map(([key, value], i) => (
              <div key={i} className="project-stat-item">
                <div className="stat-val">{value}</div>
                <div className="stat-key">{key}</div>
              </div>
            ))}
          </div>

          {/* Buttons */}
          <div className="project-actions-bento">
            <a
              href={project.preview}
              className="btn-project-bento primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 3C4.5 3 1.73 5.11 1 8c.73 2.89 3.5 5 7 5s6.27-2.11 7-5c-.73-2.89-3.5-5-7-5zm0 8.5c-1.93 0-3.5-1.57-3.5-3.5S6.07 4.5 8 4.5s3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5zm0-5.5c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"
                  fill="currentColor"
                />
              </svg>
              Preview
            </a>
            <a
              href={project.code}
              className="btn-project-bento secondary"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M5.5 11.5l-3-3 3-3M10.5 4.5l3 3-3 3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              Code
            </a>
          </div>
        </div>
      ))}
    </div>
  </div>
</section>
  )
})
